<template>
    <section class="soins-a-faire" :class="{ card: !embedded, 'soins-a-faire-embedded': embedded }" :aria-labelledby="embedded ? null : 'soins-a-faire-titre-' + _uid" :aria-label="embedded ? 'Soins à administrer' : null">
        <div class="card-body">
            <div v-if="!embedded" class="soins-entete">
                <div>
                    <h3 :id="'soins-a-faire-titre-' + _uid" class="c-title text-primary">Soins à faire</h3>
                    <p class="soins-note">{{ patientId ? 'Aperçu des soins de ce patient. Ouvrez son plan pour le détail complet.' : 'Aperçu des soins par patient. Ouvrez le plan d’administration au chevet.' }}</p>
                </div>
                <button type="button" class="soins-actualiser" :disabled="chargement" title="Actualiser les soins" aria-label="Actualiser les soins" @click="charger(1)">
                    <i class="fa fa-sync-alt" aria-hidden="true"></i>
                </button>
            </div>

            <p v-if="erreur" class="soins-erreur" role="alert">{{ erreur }} <button type="button" @click="charger(1)">Réessayer</button></p>
            <p v-if="chargement && !dossiers.length" class="soins-vide">Chargement des soins…</p>
            <p v-else-if="!erreur && !dossiers.length" class="soins-vide">{{ search && (soins.length || actes.length) ? 'Aucun patient ne correspond à la recherche.' : 'Aucun soin à afficher pour le moment.' }}</p>

            <div v-if="embedded && dossiers.length" class="soins-table-wrap">
                <table class="soins-table" aria-label="Patients avec des soins à administrer">
                    <thead><tr><th scope="col">Patient</th><th scope="col">Prise en charge</th><th scope="col">Prescrit par</th><th scope="col">Soins à suivre</th><th scope="col">Actions</th></tr></thead>
                    <tbody><tr v-for="dossier in dossiers" :key="dossier.key">
                        <td class="soins-table-patient"><strong>{{ dossier.patient }}</strong></td>
                        <td>{{ priseEnCharge(dossier) }}</td>
                        <td>{{ dossier.prescripteurs.length ? dossier.prescripteurs.join(', ') : 'Non renseigné' }}</td>
                        <td><div class="soins-table-reperes">
                            <span v-if="dossier.en_retard" class="soin-repere soin-repere-retard">{{ dossier.en_retard }} en retard</span>
                            <span v-if="dossier.maintenant" class="soin-repere soin-repere-maintenant">{{ dossier.maintenant }} à faire</span>
                            <span v-if="dossier.a_venir" class="soin-repere">{{ dossier.a_venir }} à venir</span>
                            <span v-if="dossier.actes" class="soin-repere">{{ dossier.actes }} acte{{ dossier.actes > 1 ? 's' : '' }}</span>
                            <span v-if="dossier.actesEnCours" class="soin-repere soin-repere-encours">{{ dossier.actesEnCours }} en cours</span>
                            <span v-if="dossier.etapesSansHoraire.length" class="soin-repere">Sans horaire : {{ dossier.etapesSansHoraire.join(', ') }}</span>
                        </div></td>
                        <td><router-link class="soin-ouvrir" :to="dossierPatient(dossier)">Ouvrir le plan</router-link></td>
                    </tr></tbody>
                </table>
                <p class="soins-table-count">{{ dossiers.length }} patient{{ dossiers.length > 1 ? 's' : '' }}</p>
            </div>
            <ul v-if="dossiers.length && !embedded" class="soins-liste" aria-label="Patients avec des soins à faire">
                <li v-for="dossier in dossiers" :key="dossier.key" class="soin-dossier">
                    <div class="soin-identite">
                        <h4>{{ dossier.patient }}</h4>
                        <p class="soin-consultation">{{ priseEnCharge(dossier) }}</p>
                        <p v-if="dossier.prescripteurs.length" class="soin-prescripteur">
                            Prescrit par {{ dossier.prescripteurs[0] }}<span v-if="dossier.prescripteurs.length > 1"> et {{ dossier.prescripteurs.length - 1 }} autre{{ dossier.prescripteurs.length > 2 ? 's' : '' }}</span>
                        </p>
                    </div>
                    <div class="soin-resume" aria-label="Soins repérés">
                        <span v-if="dossier.en_retard" class="soin-repere soin-repere-retard">{{ dossier.en_retard }} prise{{ dossier.en_retard > 1 ? 's' : '' }} en retard</span>
                        <span v-if="dossier.maintenant" class="soin-repere soin-repere-maintenant">{{ dossier.maintenant }} prise{{ dossier.maintenant > 1 ? 's' : '' }} à faire</span>
                        <span v-if="dossier.a_venir" class="soin-repere">{{ dossier.a_venir }} prise{{ dossier.a_venir > 1 ? 's' : '' }} à venir</span>
                        <span v-if="dossier.actes" class="soin-repere">{{ dossier.actes }} acte{{ dossier.actes > 1 ? 's' : '' }} du plan</span>
                        <span v-if="dossier.actesEnCours" class="soin-repere soin-repere-encours">{{ dossier.actesEnCours }} en cours</span>
                        <span v-if="dossier.etapesSansHoraire.length" class="soin-repere">Sans horaire : {{ dossier.etapesSansHoraire.join(', ') }}</span>
                    </div>
                    <router-link class="soin-ouvrir" :to="dossierPatient(dossier)">Ouvrir le plan</router-link>
                </li>
            </ul>

            <div v-if="total > soins.length" class="soins-suite">
                <p>{{ soins.length }} prises sur {{ total }} affichées. D’autres patients peuvent figurer dans la suite.</p>
                <button type="button" :disabled="chargement" @click="charger(page + 1, true)">Afficher la suite des prises ({{ total - soins.length }})</button>
            </div>
        </div>
    </section>
</template>

<script>
import infirmierApi from '@/api/_infirmier'

export default {
    name: 'soins-a-faire',
    props: { patientId: { type: Number, default: null }, embedded: { type: Boolean, default: false }, search: { type: String, default: '' } },
    data() {
        return {
            soins: [],
            actes: [],
            total: 0,
            page: 1,
            chargement: false,
            erreur: '',
            sequence: 0
        }
    },
    computed: {
        dossiers() {
            const groupes = new Map()
            const ajouter = item => {
                const key = String(item.patient_id) + ':' + String(item.consultation_id)
                if (!groupes.has(key)) {
                    groupes.set(key, {
                        key, patient_id: item.patient_id, consultation_id: item.consultation_id,
                        tarif_consultation_id: item.tarif_consultation_id,
                        prise_en_charge: item.prise_en_charge,
                        patient: item.patient || 'Patient n° ' + item.patient_id,
                        prescripteurs: [], etapesSansHoraire: [], en_retard: 0, maintenant: 0, a_venir: 0,
                        actes: 0, actesEnCours: 0
                    })
                }
                const groupe = groupes.get(key)
                if (!groupe.tarif_consultation_id && item.tarif_consultation_id) groupe.tarif_consultation_id = item.tarif_consultation_id
                if (!groupe.prise_en_charge && item.prise_en_charge) groupe.prise_en_charge = item.prise_en_charge
                if (item.prescripteur && !groupe.prescripteurs.includes(item.prescripteur)) groupe.prescripteurs.push(item.prescripteur)
                return groupe
            }
            this.soins.forEach(soin => {
                const groupe = ajouter(soin)
                if (soin.categorie === 'en_retard' || soin.categorie === 'maintenant' || soin.categorie === 'a_venir') {
                    groupe[soin.categorie]++
                }
                if (!soin.date_heure_hn) {
                    const etape = this.libelleEtape(soin.etape)
                    if (etape && !groupe.etapesSansHoraire.includes(etape)) groupe.etapesSansHoraire.push(etape)
                }
            })
            this.actes.forEach(acte => {
                const groupe = ajouter(acte)
                groupe.actes++
                if (acte.etat_execution === 'demarre') groupe.actesEnCours++
            })
            const priorite = groupe => groupe.en_retard ? 0 : groupe.maintenant ? 1 : groupe.actesEnCours ? 2 : groupe.a_venir ? 3 : 4
            const recherche = (this.search || '').toLocaleLowerCase('fr').trim()
            return Array.from(groupes.values()).filter(groupe => !recherche ||
                [groupe.patient, groupe.prise_en_charge, ...groupe.prescripteurs]
                    .filter(Boolean).join(' ').toLocaleLowerCase('fr').includes(recherche)).sort((a, b) =>
                priorite(a) - priorite(b) || a.patient.localeCompare(b.patient, 'fr'))
        }
    },
    watch: {
        patientId() {
            this.soins = []
            this.actes = []
            this.total = 0
            this.charger(1)
        }
    },
    mounted() { this.charger(1) },
    methods: {
        libelleEtape(etape) {
            if (!etape) return ''
            const minutes = /^\+(\d+) min$/.exec(etape)
            if (!minutes) return etape
            const valeur = Number(minutes[1])
            return valeur % 60 === 0 ? 'H' + (valeur / 60) : 'M' + valeur
        },
        priseEnCharge(item) {
            if (item.prise_en_charge) return item.prise_en_charge
            return item.consultation_id ? 'Consultation n° ' + item.consultation_id : 'Prise en charge non renseignée'
        },
        dossierPatient(item) {
            const query = item.consultation_id && item.tarif_consultation_id
                ? { inf001: item.consultation_id, tarif: item.tarif_consultation_id, suivi: 'traitement' }
                : { suivi: 'traitement' }
            return { name: 'patientInfo', params: { id: item.patient_id, consultation: 'consulter' }, query }
        },
        async charger(page, ajouter = false) {
            if (this.chargement && ajouter) return
            const sequence = ++this.sequence
            this.chargement = true
            this.erreur = ''
            try {
                const reponse = await infirmierApi.getSoinsAFaire({ page, per_page: 100, patient_id: this.patientId })
                if (sequence !== this.sequence) return
                if (!reponse || !reponse.success || !Array.isArray(reponse.data)) throw new Error('Liste des soins indisponible.')
                this.soins = ajouter ? this.soins.concat(reponse.data) : reponse.data
                this.actes = Array.isArray(reponse.actes) ? reponse.actes : []
                this.total = Number(reponse.total) || 0
                this.page = page
            } catch (error) {
                if (sequence !== this.sequence) return
                this.erreur = 'Les soins à faire n’ont pas pu être chargés.'
            } finally {
                if (sequence === this.sequence) this.chargement = false
            }
        }
    }
}
</script>

<style scoped lang="scss">
.soins-a-faire { margin-bottom: 1.5rem; border: 0; border-radius: 22px; box-shadow: none; }
.soins-a-faire .card-body { padding: 26px 30px; }
.soins-a-faire-embedded { margin: 0; border-radius: 0; background: transparent; }
.soins-a-faire-embedded .card-body { padding: 0; }
.soins-a-faire-embedded .soins-liste { margin-top: 0; }
.soins-table-wrap { overflow-x: auto; }
.soins-table { width: 100%; border-collapse: collapse; color: var(--ui-text, #344465); }
.soins-table th { background: #f2f5fb; color: var(--ui-text, #596985); font-size: .86rem; font-weight: 700; text-align: left; white-space: nowrap; }
.soins-table th, .soins-table td { padding: 12px 14px; border-bottom: 1px solid #e5ebf5; vertical-align: middle; text-align: left !important; }
.soins-table tbody tr:hover { background: #e8f1ff; }
.soins-table-patient { min-width: 190px; }
.soins-table-reperes { display: flex; flex-wrap: wrap; gap: 5px; }
.soins-table .soin-repere { margin: 0; white-space: nowrap; }
.soins-table .soin-ouvrir { white-space: nowrap; }
.soins-table-count { margin: 12px 0 0; font-size: .86rem; color: var(--ui-text-secondary, #73829d); }
.soins-entete { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; }
.soins-entete h3 { margin: 0 0 .3rem; }
.soins-note { margin: 0; color: var(--ui-text-secondary, #7685a2); font-size: .9rem; }
.soins-actualiser { width: 36px; height: 36px; border: 1px solid #d7e2f9; border-radius: 10px; background: #f6f9ff; color: #3d70ed; cursor: pointer; }
.soins-vide { padding: 1.6rem 0 .2rem; margin: 0; color: var(--ui-text-secondary, #7685a2); }
.soins-erreur { margin: 1rem 0 0; color: #b44f49; }
.soins-erreur button, .soins-suite button { border: 0; background: none; color: #3973f4; text-decoration: underline; cursor: pointer; }
.soins-liste { margin: 20px 0 0; padding: 0; list-style: none; border-top: 1px solid #dce5f5; }
.soin-dossier { display: grid; grid-template-columns: minmax(180px, 1fr) minmax(220px, 1.4fr) auto; align-items: center; gap: 18px; padding: 18px 8px; border-bottom: 1px solid #e5ebf5; }
.soin-identite h4 { margin: 0 0 3px; color: var(--ui-text, #344465); font-size: 1rem; font-weight: 700; }
.soin-identite p { margin: 0; font-size: .83rem; line-height: 1.45; }
.soin-consultation { color: var(--ui-text-secondary, #5c6e8c); }
.soin-prescripteur { color: var(--ui-text-secondary, #7785a0); }
.soin-resume { display: flex; flex-wrap: wrap; gap: 6px; }
.soin-repere { display: inline-flex; align-items: center; padding: 6px 9px; border-radius: 7px; background: #f1f4f9; color: var(--ui-text, #526582); font-size: .78rem; font-weight: 600; }
.soin-repere-retard { background: #fff0ed; color: #a84b3e; }
.soin-repere-maintenant { background: #eaf1ff; color: #315fb4; }
.soin-repere-encours { background: #e9f6ef; color: #337b57; }
.soin-ouvrir { display: inline-flex; justify-content: center; align-items: center; min-height: 36px; padding: 8px 12px; border: 1px solid #3973f4; border-radius: 8px; color: #315fb4; font-size: .85rem; font-weight: 700; white-space: nowrap; }
.soin-ouvrir:hover { background: #edf3ff; text-decoration: none; }
.soins-actualiser:disabled { opacity: .55; cursor: wait; }
.soins-suite { padding-top: 14px; text-align: center; }
.soins-suite p { margin: 0 0 5px; color: var(--ui-text-secondary, #687a98); font-size: .81rem; }
button:focus-visible, a:focus-visible { outline: 2px solid #3973f4; outline-offset: 3px; }
@media (max-width: 800px) {
    .soin-dossier { grid-template-columns: minmax(0, 1fr) auto; gap: 10px; }
    .soin-resume { grid-column: 1 / -1; grid-row: 2; }
    .soin-ouvrir { grid-column: 2; grid-row: 1; }
}
@media (max-width: 520px) {
    .soins-a-faire .card-body { padding: 20px; }
    .soins-a-faire-embedded .card-body { padding: 0; }
    .soin-dossier { grid-template-columns: 1fr; }
    .soin-resume { grid-column: 1; grid-row: auto; }
    .soin-ouvrir { grid-column: 1; grid-row: auto; justify-self: start; }
}
</style>
